import React, {useEffect, useState} from "react";
import {Auth, Hub} from "aws-amplify";
import {Authenticator} from "@aws-amplify/ui-react";
import {appCtx, isAWS, isAwsDev, isOnPrem} from "./AppContext";
import Keycloak from "keycloak-js";
import {awsAuth, awsAuthHosted} from "../aws-exports";
import "@aws-amplify/ui-react/styles.css";
import "./AwsUser.scss";
import {Divider} from "./CommonUIComponenets";
import gytpolScreenLight from "../img/gytpol_screen_light.jpg";
import gytpolScreenDark from "../img/gytpol_screen_dark.jpg";
import {ReactComponent as RemedioLogoWhite} from "../img/Remedio-White-Logo.svg";
import {ReactComponent as RemedioLogoColor} from "../img/Remedio-Color-Logo.svg";
import coolVendorBadge from "../img/gartner-cool-vendor-badge.png";
import {useColorModeTheme} from "../tools/CommonReact";
import {Button} from "../tools/Inputs";
import {LoadingAnim} from "../tools/CommonHtml";
import {connectWebSock} from "../tools/socket";
//function Keycloak() { return {}};
// TODO: make Keycloak and Amplify conditional with dynamic imports.


export function CheckAuth({children}) {
    return import.meta.env.VITE_AUTH_OIDC === "hosted"?
        <HostedAuth {...{children}}/> :
        isAWS? <CognitoAuth {...{children}}/> :
            isOnPrem? <OnPremAuth {...{children}}/> :
                children;
}


function useKeycloak() {
    const [keycloak, setKeycloak] = useState(null);

    useEffect(() => {
        const keycloak = new Keycloak("/keycloak.json");
        console.log("keycloak- starting authentication...");

        keycloak.init({onLoad: "login-required"})
            .then(authenticated => {
                console.log("keycloak - authenticated:", authenticated);
                setKeycloak(keycloak);
                afterAuth({
                    user: null,
                    signOut: () => {
                        keycloak.logout();
                        setKeycloak(null);
                    }
                },
                          async () => {
                              await keycloak.updateToken(10);
                              return keycloak.idToken;
                          });
            });
    }, []);

    return keycloak;
}

function OnPremAuth({children}) {
    const keycloak = useKeycloak();

    return keycloak?.authenticated? children :
        <LoadingAnim classNameSize="w-10 h-10" className="mt-20"/>;
}


if (isAWS)
    Auth.configure(import.meta.env.VITE_AUTH_OIDC === "hosted" ? awsAuthHosted : awsAuth);
//Auth.configure({ storage: window.sessionStorage });           // user token will be saved in sessionStorage instead of localStorage

async function getAWSJwtToken() {
    const session = await Auth.currentSession();
    return session.idToken.jwtToken;
}

const LoginBackground = ({curTheme, children}) => (
    <div className="w-full h-full flex justify-center p-10 2xl:p-20 items-center">
        <img
            style={{userDrag: "none", "-webkit-user-drag": "none"}} alt="Remedio Screenshot"
            className="absolute w-full h-full blur select-none" src={curTheme !== "dark" ? gytpolScreenLight : gytpolScreenDark}/>
        <div
            style={{backdropFilter: "blur(8px"}}
            className="w-full h-full flex flex-col items-center justify-center gap-8 p-6 bg-gray-300/15 border-white/10 border rounded-2xl shadow-md border-1">
            {children}
        </div>
    </div>);

function HostedAuth({children}) {
    const [isSignedIn, setIsSignedIn] = useState(false);
    const {curTheme} = useColorModeTheme();

    //const signIn = () => Auth.federatedSignIn({provider: "azuread"});       // remove provider to get to Cognito's Hosted UI.
    const signIn = () => Auth.federatedSignIn();       // remove provider to get to Cognito's Hosted UI.
    const signOut = () => (setIsSignedIn(false), Auth.signOut());

    useEffect(() => {
        Hub.listen("auth", ({payload: {event, data}}) => {
            //console.log("Hub", event);
            if (event === "signIn_failure")
                console.log("Sign in failure", data);
        });

        // Check if authenticated. UI gets reloaded after redirected from hosted sign-in, so this gets executed again.
        Auth.currentAuthenticatedUser()      // {bypassCache: true}
            .then(user => {
                //console.log("Auth:", user, user.signInUserSession?.idToken?.payload?.email);
                // appCtx.nav.isInLogin = false;
                afterAuth({user, signOut}, getAWSJwtToken);
                setIsSignedIn(true);
            })
            .catch(() => 0);
    }, []);

    return isSignedIn ? children : appCtx.nav.isInLogin ? null :
        <LoginBackground curTheme={curTheme}>
            <div className="flex relative items-center h-1/6">
                <img src={coolVendorBadge} className="opacity-80 h-16 absolute -right-24 mt-2" alt="Gartner Cool Vendor Badge"/>
                {React.createElement(curTheme !== "dark" ? RemedioLogoColor : RemedioLogoWhite, {className: "h-full"})}
            </div>
            <Divider className="w-full" classNameColor="bg-title"/>
            <div className="text-center text-4xl font-medium text-title">
                Welcome to {import.meta.env.VITE_DESC}
            </div>
            <p className="italic text-2xl text-gray-300">Please sign in with your organizational credentials</p>
            <Button onClick={signIn} className="px-6 py-2 text-2xl text-prm-contrast bg-prm-main">Login</Button>
        </LoginBackground>;
}

function CognitoAuth({children}) {
    const {curTheme} = useColorModeTheme();

    return (
        <Authenticator
            className="p-0" hideSignUp={true} components={
                {Header: () =>
                    <>
                        <div className="absolute h-[100vh] w-[100vw] z-[-1] login-background">
                            <LoginBackground curTheme={curTheme}/>
                        </div>
                        <div className="flex justify-between items-center mb-2 gap-2">
                            {React.createElement(curTheme !== "dark"? RemedioLogoColor : RemedioLogoWhite, {className: "h-20"})}
                            <img src={coolVendorBadge} className="opacity-80 h-16" alt="Gartner Cool Vendor Badge"/>
                        </div>
                    </>

                }}>
            {aws => (afterAuth(aws, getAWSJwtToken), children)}
        </Authenticator>
    );
}

function afterAuth(auth, getIdJwtToken) {
    appCtx.auth = auth;
    appCtx.auth.getIdJwtToken = getIdJwtToken;

    appCtx.socket = (isAWS || isAwsDev || isOnPrem) && connectWebSock();
    //console.log(aws.user)
    // appCtx.aws.jwtToken = aws.user.signInUserSession?.idToken?.jwtToken;
    //ctx.aws.userId = aws.user.signInUserSession?.idToken?.payload?.email;
    if (appCtx.auth) setTimeout(() => {
        appCtx.user?.refetch();
        appCtx.license?.refetch();
        appCtx.domains?.refetch();
    });
    //console.log("Authenticator:", aws);
}
